import {
  FaCheckCircle,
  FaRegClock,
  FaShieldAlt,
  FaTasks,
  FaUserCheck,
} from "react-icons/fa";
import Seo from "../../components/Seo";
import ServicePageTemplate from "../../components/ServicePageTemplate";
import { createBreadcrumbSchema } from "../../lib/seo";

const seo = {
  title: "Expert Virtual Assistant Services for Healthcare | Physicians Success Partners",
  description:
    "Physicians Success Partners offers reliable virtual assistant solutions to streamline administrative tasks, improve efficiency, and support healthcare practices.",
  keywords:
    "virtual assistant service, healthcare virtual assistant, remote administrative support",
  schema: [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Virtual Assistant Services",
      url: "https://psprcm.us/services/virtual-assistant/",
      description:
        "Physicians Success Partners offers reliable virtual assistant solutions to streamline administrative tasks, improve efficiency, and support healthcare practices.",
      serviceType: "Virtual Assistant",
      provider: {
        "@type": "Organization",
        name: "Physicians Success Partners",
        url: "https://psprcm.us/",
        logo: "https://psprcm.us/logo/main-logo.webp",
        telephone: "+1 6155536787",
        email: "info@psprcm.us",
        address: {
          "@type": "PostalAddress",
          streetAddress: "1616 Westgate Circle",
          addressLocality: "Brentwood",
          addressRegion: "TN",
          postalCode: "37027",
          addressCountry: "USA",
        },
      },
      areaServed: {
        "@type": "Country",
        name: "United States",
      },
      audience: {
        "@type": "MedicalAudience",
        audienceType: "Healthcare Providers",
      },
      availableChannel: {
        "@type": "ServiceChannel",
        serviceLocation: {
          "@type": "Place",
          address: { "@type": "PostalAddress", addressCountry: "USA" },
        },
        availableLanguage: ["English"],
      },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Virtual Assistant Solutions",
        itemListElement: [
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Patient Scheduling & Appointment Reminders" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Insurance Verification Support" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Billing & Claims Assistance" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Administrative Workflow Management" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Patient Communication & Follow-Up" } },
        ],
      },
    },
    createBreadcrumbSchema("/services/virtual-assistant", "Expert Virtual Assistant Services for Healthcare"),
  ],
};

export default function VirtualAssistant() {
  return (
    <>
      <Seo
        title={seo.title}
        metaTitle={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonicalPath="/services/virtual-assistant"
        schema={seo.schema}
      />
      <ServicePageTemplate
        banner={{
        heading: "Virtual Assistant Service",
        paragraph:
          "Strengthen day-to-day operations with remote administrative support for scheduling, coordination, communication, and workflow management.",
        image: "/img/services-img/virtual-assistant.webp",
      }}
      grid={{
        kicker: "Operational Support",
        heading: "Extend Your Team Without Increasing Front-Office Strain",
        description:
          "Virtual assistant support helps practices manage repetitive operational work without overloading in-house teams. We assist with daily coordination, communication, and administrative workflows so your staff can stay focused on higher-priority patient and practice needs.",
        metrics: [
          { icon: <FaTasks />, value: "Daily", label: "Task Coordination", style: "metricPrimary" },
          { icon: <FaUserCheck />, value: "More", label: "Team Capacity", style: "metricAccent" },
          { icon: <FaRegClock />, value: "Faster", label: "Operational Response", style: "metricLight" },
          { icon: <FaShieldAlt />, value: "100%", label: "Workflow Reliability", style: "metricDark" },
        ],
        panelEyebrow: "Administrative Relief",
        panelHeading: "We help your practice stay organized and responsive with remote support that fits evolving operational needs.",
        assurances: [
          { icon: <FaCheckCircle />, label: "Scheduling Support" },
          { icon: <FaTasks />, label: "Workflow Coordination" },
          { icon: <FaUserCheck />, label: "Scalable Team Support" },
        ],
      }}
      services={{
        heading: "Advantages of PSP Virtual Assistant Services",
        description:
          "Virtual assistants help practices manage repetitive administrative work without overloading in-house staff. We support operational flow with dependable remote assistance that keeps routine tasks organized and moving.",
        items: [
          { title: "Administrative Relief", text: "We take on recurring support tasks so internal teams can focus more attention on patient care and high-priority responsibilities." },
          { title: "Flexible Remote Support", text: "Our virtual assistant model gives practices operational help without the overhead of adding more on-site administrative staff." },
          { title: "Improved Task Coordination", text: "From scheduling to communication workflows, we help keep daily operations more organized and easier to manage." },
          { title: "Scalable Workflow Coverage", text: "Practices can extend support capacity during growth, staffing changes, or busy periods while maintaining consistent service execution." },
        ],
      }}
      faq={{
        image: "/img/services-img/virtual-assistant.webp",
        imageAlt: "Virtual assistant support",
        title: "Why Choose PSP for Virtual Assistant Services?",
        question: [
          "What kinds of tasks can a virtual assistant support?",
          "How does PSP help practices stay organized day to day?",
          "Can this service reduce front-office workload pressure?",
        ],
        answer: [
          "Virtual assistants can support scheduling, coordination, follow-up, communication tasks, and other recurring administrative workflows.",
          "We provide dependable remote support that helps practices maintain task visibility, smoother coordination, and better operational consistency.",
          "Absolutely. By taking on routine operational work, we help reduce pressure on front-office teams and improve day-to-day workflow balance.",
        ],
      }}
      specialtiesContent={{
        eyebrow: "Operational Support",
        heading: "Virtual assistant workflows that help specialty practices stay responsive and organized.",
        description:
          "We extend administrative capacity with remote support that adapts to the pace and needs of healthcare operations.",
        }}
      />
    </>
  );
}
